import { Injectable } from '@angular/core'
import {
  ICalendarDay,
  ICalendarMonthGroup,
  ICalendarWeekGroup
} from '../interfaces/location-daily-targets.interface'
import { IMonthlyTarget, IObjectiveConfig } from '../interfaces/yearly-targets-page.interface'

export const MONTH_SHORT_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

export const MONTH_FULL_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export const DAY_OF_WEEK_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export type DayTierLookup = Record<string, { tier1: number; tier2: number; tier3: number }>

export interface IRebuiltCalendar {
  monthGroups: ICalendarMonthGroup[]
  weekGroups: ICalendarWeekGroup[]
  monthlyTargets: IMonthlyTarget
  annualTier1: number
  annualTier2: number
  annualTier3: number
}

@Injectable({ providedIn: 'root' })
export class TargetCalendarService {
  /**
   * Generates the ISO-week calendar for a year — from Monday of ISO Week 1
   * through Sunday of the week containing Dec 31. Hydrates tier values from
   * dayLookup (keyed by `YYYY-MM-DD`) when provided.
   */
  generateCalendarDays(
    year: number,
    isSaturdayOpen: boolean,
    dayLookup?: DayTierLookup
  ): ICalendarDay[] {
    const days: ICalendarDay[] = []

    const jan4 = new Date(year, 0, 4)
    const jan4dow = jan4.getDay() || 7
    const startDate = new Date(year, 0, 4 - jan4dow + 1)

    const dec31 = new Date(year, 11, 31)
    const dec31dow = dec31.getDay() || 7
    const endDate = new Date(dec31)
    if (dec31dow < 7) endDate.setDate(endDate.getDate() + (7 - dec31dow))

    const current = new Date(startDate)
    while (current <= endDate) {
      const jsDow = current.getDay()
      const isoDow = jsDow === 0 ? 6 : jsDow - 1
      const isSat = isoDow === 5
      const isSun = isoDow === 6
      const isWorkingDay = !isSun && !(isSat && !isSaturdayOpen)

      // ISO week number: Thursday of the current week determines the ISO year.
      const d = new Date(Date.UTC(current.getFullYear(), current.getMonth(), current.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const isoYear = d.getUTCFullYear()
      const yearStart = new Date(Date.UTC(isoYear, 0, 1))
      const isoWeek = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
      const weekNumber = isoYear * 100 + isoWeek

      const yyyy = current.getFullYear()
      const mm = String(current.getMonth() + 1).padStart(2, '0')
      const dd = String(current.getDate()).padStart(2, '0')
      const apiDay = dayLookup ? dayLookup[`${yyyy}-${mm}-${dd}`] : undefined

      days.push({
        date: new Date(current),
        year: current.getFullYear(),
        month: current.getMonth(),
        dayOfMonth: current.getDate(),
        dayOfWeek: isoDow,
        dayLabel: `${DAY_OF_WEEK_NAMES[isoDow]}, ${current.getDate()} ${MONTH_SHORT_NAMES[current.getMonth()]}`,
        weekNumber,
        isoWeek,
        isWorkingDay,
        tier1: apiDay ? apiDay.tier1 : 0,
        tier2: apiDay ? apiDay.tier2 : 0,
        tier3: apiDay ? apiDay.tier3 : 0,
        gridRow: days.length + 2
      })

      current.setDate(current.getDate() + 1)
    }

    return days
  }

  /**
   * Derives month groups, ISO-week groups, chart-ready monthlyTargets, and
   * annual tier totals from a flat day list. Respects `objective.aggregation`
   * (sum vs average over working days) and pulls previous-year per-month
   * values from `previousYearByMonthAbbr` (keyed by 3-letter month name).
   */
  rebuildGroupsFromDays(
    year: number,
    calendarDays: ICalendarDay[],
    objective: IObjectiveConfig,
    previousYearByMonthAbbr: IMonthlyTarget
  ): IRebuiltCalendar {
    const isAvg = objective.aggregation === 'average'

    const monthGroups = this.groupByMonth(year, calendarDays)
    this.computeMonthTotals(monthGroups, year, isAvg, previousYearByMonthAbbr)

    const monthlyTargets: IMonthlyTarget = {}
    for (const mg of monthGroups) {
      if (mg.year === year) {
        monthlyTargets[MONTH_SHORT_NAMES[mg.month]] = Math.round(mg.tier1Total * 100) / 100
      }
    }

    const weekGroups = this.groupByWeek(calendarDays)
    this.computeWeekTotals(weekGroups, isAvg)

    const yearMonths = monthGroups.filter((mg) => mg.year === year)
    const annual = this.computeAnnualTotals(yearMonths, isAvg)

    return { monthGroups, weekGroups, monthlyTargets, ...annual }
  }

  private groupByMonth(year: number, days: ICalendarDay[]): ICalendarMonthGroup[] {
    const monthGroups: ICalendarMonthGroup[] = []
    let curr: ICalendarMonthGroup | null = null
    for (const day of days) {
      const key = `${day.year}-${day.month}`
      if (!curr || curr.key !== key) {
        curr = {
          key,
          year: day.year,
          month: day.month,
          label: `${MONTH_FULL_NAMES[day.month]} ${day.year}`,
          shortLabel: `${MONTH_SHORT_NAMES[day.month]}${
            day.year !== year ? " '" + String(day.year).slice(2) : ''
          }`,
          days: [],
          tier1Total: 0,
          tier2Total: 0,
          tier3Total: 0,
          previousYearValue: 0,
          growth: 0
        }
        monthGroups.push(curr)
      }
      curr.days.push(day)
    }
    return monthGroups
  }

  private computeMonthTotals(
    monthGroups: ICalendarMonthGroup[],
    year: number,
    isAvg: boolean,
    previousYearByMonthAbbr: IMonthlyTarget
  ): void {
    for (const mg of monthGroups) {
      const workingDays = mg.days.filter((d) => d.isWorkingDay)
      const sum1 = workingDays.reduce((s, d) => s + d.tier1, 0)
      const sum2 = workingDays.reduce((s, d) => s + d.tier2, 0)
      const sum3 = workingDays.reduce((s, d) => s + d.tier3, 0)
      if (isAvg) {
        const c = workingDays.length || 1
        mg.tier1Total = sum1 / c
        mg.tier2Total = sum2 / c
        mg.tier3Total = sum3 / c
      } else {
        mg.tier1Total = sum1
        mg.tier2Total = sum2
        mg.tier3Total = sum3
      }

      if (mg.year === year) {
        mg.previousYearValue = previousYearByMonthAbbr[MONTH_SHORT_NAMES[mg.month]] || 0
        mg.growth =
          mg.previousYearValue > 0
            ? ((mg.tier1Total - mg.previousYearValue) / mg.previousYearValue) * 100
            : 0
      }
    }
  }

  private groupByWeek(days: ICalendarDay[]): ICalendarWeekGroup[] {
    const weekGroups: ICalendarWeekGroup[] = []
    let curr: ICalendarWeekGroup | null = null
    for (const day of days) {
      if (!curr || curr.weekNumber !== day.weekNumber) {
        curr = {
          weekNumber: day.weekNumber,
          isoWeek: day.isoWeek,
          days: [],
          tier1: 0,
          tier2: 0,
          tier3: 0,
          isReadOnly: false
        }
        weekGroups.push(curr)
      }
      curr.days.push(day)
    }
    return weekGroups
  }

  private computeWeekTotals(weekGroups: ICalendarWeekGroup[], isAvg: boolean): void {
    for (const wg of weekGroups) {
      const workingDays = wg.days.filter((d) => d.isWorkingDay)
      const sum1 = workingDays.reduce((s, d) => s + d.tier1, 0)
      const sum2 = workingDays.reduce((s, d) => s + d.tier2, 0)
      const sum3 = workingDays.reduce((s, d) => s + d.tier3, 0)
      const c = workingDays.length || 1
      wg.tier1 = isAvg ? sum1 / c : sum1
      wg.tier2 = isAvg ? sum2 / c : sum2
      wg.tier3 = isAvg ? sum3 / c : sum3
    }
  }

  private computeAnnualTotals(
    yearMonths: ICalendarMonthGroup[],
    isAvg: boolean
  ): { annualTier1: number; annualTier2: number; annualTier3: number } {
    const round = (v: number) => Math.round(v * 100) / 100
    const sum1 = yearMonths.reduce((s, mg) => s + mg.tier1Total, 0)
    const sum2 = yearMonths.reduce((s, mg) => s + mg.tier2Total, 0)
    const sum3 = yearMonths.reduce((s, mg) => s + mg.tier3Total, 0)
    if (isAvg) {
      const c = yearMonths.length || 1
      return { annualTier1: round(sum1 / c), annualTier2: round(sum2 / c), annualTier3: round(sum3 / c) }
    }
    return { annualTier1: round(sum1), annualTier2: round(sum2), annualTier3: round(sum3) }
  }
}
